import { useMemo, useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { PEN_COLOURS, TrendChart, type Pen } from './TrendChart';

const WINDOWS: { label: string; seconds: number }[] = [
  { label: '5 min', seconds: 300 },
  { label: '15 min', seconds: 900 },
  { label: '1 h', seconds: 3600 },
  { label: '4 h', seconds: 4 * 3600 },
  { label: '8 h', seconds: 8 * 3600 },
  { label: '24 h', seconds: 24 * 3600 },
  { label: '72 h', seconds: 72 * 3600 },
];

export function TrendsScreen(): JSX.Element {
  const plant = useEngine((s) => s.plant);
  const trendTags = useEngine((s) => s.trendTags);
  const groupId = useEngine((s) => s.selectedTrendGroup);
  const setGroup = useEngine((s) => s.setTrendGroup);
  const customPens = useEngine((s) => s.customPens);
  const setCustomPens = useEngine((s) => s.setCustomPens);
  const requestTrend = useEngine((s) => s.requestTrend);
  const simTime = useEngine((s) => s.snap?.simTime ?? 0);

  const [windowS, setWindowS] = useState(3600);
  const [offsetS, setOffsetS] = useState(0);
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const group = plant?.trendGroups?.find((g) => g.id === groupId);
  // Memoised: a fresh array here would change the pen identities on every render and
  // restart the trend request loop.
  const tags = useMemo(
    () => (customPens.length > 0 ? customPens : (group?.tags ?? [])),
    [customPens, group],
  );

  const pens: Pen[] = useMemo(
    () =>
      tags.slice(0, 8).map((tag, i) => ({
        tag,
        colour: PEN_COLOURS[i % PEN_COLOURS.length] as string,
        lo: null,
        hi: null,
        visible: !hidden.has(tag),
      })),
    [tags, hidden],
  );

  const matches = useMemo(() => {
    if (search.trim().length < 1) return [];
    const q = search.toUpperCase();
    return trendTags.filter((t) => t.toUpperCase().includes(q)).slice(0, 60);
  }, [search, trendTags]);

  const exportCsv = async (): Promise<void> => {
    const to = Math.max(0, simTime - offsetS);
    const series = await requestTrend(tags, Math.max(0, to - windowS), to, 20_000);
    const first = series[0];
    if (!first) return;
    const head = ['simTime_s', ...series.map((s) => s.tag)].join(',');
    const lines = [head];
    for (let i = 0; i < first.t.length; i++) {
      lines.push([(first.t[i] as number).toFixed(1), ...series.map((s) => (s.mean[i] as number)?.toPrecision(8) ?? '')].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `setpoint-trend-${Math.round(to)}s.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: 10, gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {WINDOWS.map((w) => (
            <button
              key={w.label}
              className={windowS === w.seconds ? 'on' : ''}
              onClick={() => {
                setWindowS(w.seconds);
                setOffsetS(0);
              }}
            >
              {w.label}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <button disabled={offsetS === 0} onClick={() => setOffsetS(0)}>
            Live
          </button>
          <button onClick={() => void exportCsv()}>Export CSV</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, border: '1px solid var(--line-dim)', borderRadius: 4, overflow: 'hidden' }}>
          <TrendChart
            pens={pens}
            windowS={windowS}
            offsetS={offsetS}
            height={0}
            onPan={(d) => setOffsetS((o) => Math.max(0, o + d))}
            onZoom={(f) => setWindowS((w) => Math.max(60, Math.min(72 * 3600, w * f)))}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {pens.map((p) => (
            <button
              key={p.tag}
              onClick={() =>
                setHidden((h) => {
                  const n = new Set(h);
                  if (n.has(p.tag)) n.delete(p.tag);
                  else n.add(p.tag);
                  return n;
                })
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                border: 'none',
                opacity: p.visible ? 1 : 0.4,
                padding: 0,
              }}
            >
              <span style={{ width: 16, height: 3, background: p.colour, borderRadius: 2 }} />
              <span className="num" style={{ fontSize: 11.5 }}>
                {p.tag}
              </span>
            </button>
          ))}
        </div>
      </div>

      <aside
        className="scroll hmi-aside"
        style={{ width: 260, borderLeft: '1px solid var(--line-dim)', padding: 10, background: 'var(--panel)' }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Saved groups</div>
        {(plant?.trendGroups ?? []).map((g) => (
          <button
            key={g.id}
            className={customPens.length === 0 && groupId === g.id ? 'on' : ''}
            onClick={() => {
              setCustomPens([]);
              setGroup(g.id);
              setHidden(new Set());
            }}
            style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }}
          >
            {g.name}
          </button>
        ))}

        <div style={{ fontWeight: 600, margin: '14px 0 6px' }}>Add a pen</div>
        <input
          placeholder="Search tags…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%', marginBottom: 6 }}
        />
        <div className="scroll" style={{ maxHeight: 240 }}>
          {matches.map((t) => (
            <button
              key={t}
              onClick={() => {
                const base = customPens.length > 0 ? customPens : (group?.tags ?? []);
                if (base.includes(t)) return;
                setCustomPens([...base, t].slice(0, 8));
              }}
              className="num"
              style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 11.5, marginBottom: 2, background: 'transparent', border: 'none' }}
            >
              {t}
            </button>
          ))}
        </div>
        {customPens.length > 0 && (
          <button style={{ marginTop: 8 }} onClick={() => setCustomPens([])}>
            Back to the group
          </button>
        )}
      </aside>
    </div>
  );
}
